import { ArrowRightIcon, BanknoteIcon, CreditCardIcon, PlusIcon, ShieldIcon } from 'lucide-react'
import { Button } from '@klear/ui/components/button'
import { Card, CardContent, CardHeader, CardTitle } from '@klear/ui/components/card'
import { Link, useParams } from 'react-router'
import { ROUTE_IDENTITY_VERIFICATION } from '@klear/constants'
import { useTrader } from '@klear/ui/hooks/use-trader'
import {
  useKlearFinancialAccount,
  useKlearFinancialAccountMutations
} from '@klear/ui/hooks/use-klear-financial-account'
import { useRef } from 'react'
import { useKyc } from '@klear/ui/hooks/use-kyc'
import { LoadingDots } from '@klear/ui/components/loading-dots'
import noop from 'lodash/noop'

export default function KlearAccountLanding() {
  const { traderId } = useParams()
  const { urlPrefixApp } = useTrader(traderId)
  const { kyc } = useKyc()

  const { klearFinancialAccount, isLoading: klearBankAccountLoading } = useKlearFinancialAccount()
  const { createKlearFinancialAccount: createKlearBankAccount, isCreating } = useKlearFinancialAccountMutations()

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const handleAddKlearBankAccount = async () => {
    if (!kyc?.canCreateKlearAccount) {
      return
    }
    try {
      await createKlearBankAccount()

      timeoutRef.current = setTimeout(noop, 50000)
    } catch (error) {
      console.error('Failed to create Klear account:', error)
    }
  }

  return (
    <div className="flex flex-col p-6 gap-8 max-w-4xl mx-auto">
      <div className="text-center space-y-4">
        <h1 className="text-3xl font-bold">Welcome to Klear Account</h1>
        <p className="text-lg text-muted-foreground">
          Your all-in-one financial hub for managing payments, transfers, and more
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <BanknoteIcon className="h-5 w-5 text-[#0066FF]" />
              <CardTitle className="text-lg">Easy Payments</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Send and receive payments instantly with Klear Pay. Perfect for business transactions.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <CreditCardIcon className="h-5 w-5 text-[#0066FF]" />
              <CardTitle className="text-lg">Smart Banking</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Manage your funds with a modern banking experience designed for businesses.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldIcon className="h-5 w-5 text-[#0066FF]" />
              <CardTitle className="text-lg">Secure & Reliable</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              Bank-grade security and 24/7 support to keep your transactions safe.
            </p>
          </CardContent>
        </Card>
      </div>
      {!kyc?.isVerified && !klearFinancialAccount && (
        <div className="flex flex-col items-center gap-4">
          <Link to={`${urlPrefixApp}/${ROUTE_IDENTITY_VERIFICATION}`}>
            <Button size="lg">
              Complete Identity Verification
              <ArrowRightIcon className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      )}
      {kyc?.canCreateKlearAccount && !klearFinancialAccount && (
        <Card className="max-w-3xl mx-auto">
          <CardContent className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <h2 className="text-3xl font-bold tracking-tight mb-4">Open a Klear Financial Account</h2>
            <Button onClick={handleAddKlearBankAccount} disabled={klearBankAccountLoading || isCreating} size="lg">
              <PlusIcon className="w-5 h-5 mr-2" />
              {isCreating ? <LoadingDots text="Creating Account" /> : 'Open Account'}
            </Button>
          </CardContent>
        </Card>
      )}
      {kyc?.klearAccountNotAvailableInCountry && !klearFinancialAccount && (
        <div className="rounded-md bg-yellow-50 dark:bg-yellow-950 p-4 mt-6">
          <div className="flex">
            <div className="flex-shrink-0 space-y-2">
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                Klear account onboarding is not available in your country.
              </p>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                Contact support to find out more about international accounts.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
